<template>
  <vee-form v-slot="{ validate }" as="">
    <form
      ref="formRef"
      class="payment-details standard-form billing-address-entry"
      method="post"
      @submit.prevent="handleSubmit(validate)"
    >
      <!-- error state -->
      <callout v-if="error" type="error">
        <div>{{ error }}</div>
      </callout>

      <!-- loading -->
      <spinner-loader v-if="initLoading" />

      <!-- countries loaded successfully -->
      <div v-if="isReady">
        <text-input
          v-model="billingAddress.city"
          input-id="city"
          label="City Name"
          placeholder="City Name"
          rules="required|max:128"
          validate-as="City"
          maxlength="128"
          data-hj-suppress
          autocomplete="billing address-level2"
        />

        <select-input
          v-if="showProvinceList"
          v-model.number="provinceAbbr"
          input-id="stateProvince"
          label="State / Province / Region"
          :rules="provinceRequiredRules"
          :options="provinceOptions"
          data-hj-suppress
          autocomplete="billing address-level1"
        />

        <text-input
          v-else
          v-model="billingAddress.custom_province_alpha_code"
          input-id="stateProvinceCustom"
          label="State / Province / Region Code"
          placeholder="2-3 Character State or Province Code (optional)"
          rules="max:3"
          validate-as="Province"
          maxlength="3"
          data-hj-suppress
          autocomplete="billing address-level1"
        />
        <!-- end province -->

        <!-- postal code -->
        <text-input
          v-model="billingAddress.postal_code"
          input-id="postal_code"
          label="Postal Code"
          placeholder="Postal or Zip Code"
          rules="required|max:10"
          validate-as="Postal code"
          maxlength="10"
          data-hj-suppress
          autocomplete="billing postal-code"
        />

        <!-- country drop down with typing support -->
        <input-slot
          :value="billingAddress.country_id"
          input-id="country"
          label="Country"
          rules="required"
        >
          <multiselect
            id="country"
            v-model="selectedCountry"
            name="country"
            select-label="Select"
            deselect-label="Remove"
            placeholder="Select Country"
            :options="countries"
            :loading="loadingProvinces"
            :multiple="false"
            track-by="id"
            label="name"
            :allow-empty="false"
            @select="setCountryAndFetchProvinces"
          ></multiselect>
        </input-slot>

        <!-- form actions -->
        <div v-if="actionType === 'cart'" class="group -flex -between -vcenter">
          <div class="cancel-order">
            <button role="button" type="button" class="btn -anchor-link" @click="handleCancel">
              Cancel
            </button>
          </div>
          <submit-button
            :submitting="processing"
            label="Proceed to Payment"
            submitting-label="Processing…"
            class="btn -main -large-medium-only no-margin"
            @submit-action="handleSubmit(validate)"
          />
        </div>

        <!-- Subscription CTAs, no cancel -->
        <div v-if="actionType === 'subscription'" class="group -flex -between -vcenter">
          <submit-button
            :submitting="processing"
            label="Proceed to Payment"
            submitting-label="Processing…"
            class="btn -main -block"
            @submit-action="handleSubmit(validate)"
          />
        </div>

        <!-- Subscription CTAs, no cancel -->
        <div v-if="actionType === 'updatePaymentMethod'" class="group -flex -between -vcenter">
          <submit-button
            :submitting="processing"
            label="Proceed to Payment"
            submitting-label="Processing…"
            class="btn -main"
            @submit-action="handleSubmit(validate)"
          />
        </div>
      </div>
    </form>
  </vee-form>
</template>

<script>
import { defineComponent } from 'vue'
import { Form as VeeForm } from 'vee-validate'
import Multiselect from 'vue-multiselect'
import Callout from '@/components/notifications/callout.vue'
import SpinnerLoader from '@/components/loaders/spinner-loader.vue'
import TextInput from '@/components/forms/text-input.vue'
import SubmitButton from '@/components/buttons/submit-button.vue'
import { fbApi } from '@/utils/http-api'
import SelectInput from '@/components/forms/select-input.vue'
import InputSlot from '@/components/forms/input-slot.vue'

/**
 * Billing address form that handles loading countries and provinces.
 *
 * Emits the following events
 * - billing-submit-action
 * - billing-cancel-action
 *
 * Note that emitted billing-submit-action can be called on create lifecycle hook
 * if autoSubmit is true, see prop for details.
 *
 * For autocomplete, using browsers
 * @see https://cloudfour.com/thinks/autofill-what-web-devs-should-know-but-dont/
 * - tested with chrome and safari
 */
export default defineComponent({
  name: 'BillingAddressForm',
  components: {
    InputSlot,
    SelectInput,
    VeeForm,
    Multiselect,
    Callout,
    SpinnerLoader,
    TextInput,
    SubmitButton,
  },
  props: {
    // initial form data
    initialBillingAddress: {
      type: Object,
      default: () => ({}),
    },

    // Useful if you want to skip the billing step and go straight to payment step
    // if true
    // - this form will set state based on initialBillingAddress
    // - assume it is all correct
    // - will show the initial loading state (users will never see form)
    // - will pluck country and province data from API calls
    // - will format the billingAddress data as if the form was submitted
    // - $emits billing-submit-action so parent component can handle
    autoSubmit: {
      type: Boolean,
      default: true,
    },

    // determines the action buttons used
    actionType: {
      default: 'cart',
      type: String,
      validator: (val) => ['cart', 'subscription', 'updatePaymentMethod'].includes(val),
    },
  },

  emits: ['billing-submit-action', 'billing-cancel-action'],

  /* state */
  data() {
    // default to USA unless a country is passed in via initialBillingAddress
    const usaCountryId = 236

    return {
      // initial loading
      initLoading: false,
      loadingCountries: false,
      loadingProvinces: false,
      error: '',

      // only require province (drop down) from the following countries
      requiredProvinceCountryIds: [usaCountryId],

      // array of countries
      countries: [],

      // hash of provinces by country id, { 236: [], 4: [] }
      provinceMap: {},

      // form submitting and data
      processing: false,
      billingAddress: {
        // defaults
        city: '',

        // is populated from provinceAbbr, but not directly from form
        province_id: '',

        // user entered province id
        custom_province_alpha_code: '',

        postal_code: '',
        country_id: usaCountryId,

        // from initial state, overrides defaults
        ...this.initialBillingAddress,
      },

      // used in form for multiselect
      selectedCountry: null,

      // used in form to aid in browser autofill (abbr value has more success)
      provinceAbbr: '',

      // when provinceAbbr changes, so will this, see watcher
      selectedProvince: null,
    }
  },

  /* computed */
  computed: {
    // make sure country has fully loaded
    isReady() {
      return this.countries && this.countries.length && !this.error && !this.initLoading
    },

    // toggle province list vs text entry
    showProvinceList() {
      return (
        this.provinceMap &&
        this.provinceMap[this.billingAddress.country_id] &&
        this.provinceMap[this.billingAddress.country_id].length
      )
    },

    // only require some countries
    provinceRequiredRules() {
      if (this.showProvinceList) {
        const isRequired = this.requiredProvinceCountryIds.some(
          (countryId) => countryId === this.billingAddress.country_id,
        )

        return isRequired ? 'required' : ''
      }

      return ''
    },

    provinceOptions() {
      if (!this.showProvinceList) {
        return []
      }

      return [{ value: '', label: 'Select One' }].concat(
        this.provinceMap[this.billingAddress.country_id].map((province) => {
          return {
            value: province.alphaCode,
            label: province.name,
          }
        }),
      )
    },
  },

  watch: {
    // force uppercase provinces
    'billingAddress.custom_province_alpha_code': function (input) {
      input = input.toUpperCase()
      this.billingAddress.custom_province_alpha_code = input
    },

    // side effects when changing form provinceAbbr, set selectedProvince and update province_id
    provinceAbbr: function (provinceCode) {
      this.selectedProvince = this.findProvinceByAbbr(this.billingAddress.country_id, provinceCode)

      // set billing address id needed for db
      if (this.selectedProvince) {
        this.billingAddress.province_id = this.selectedProvince.id
      }
    },
  },

  /* lifecycle */
  created() {
    this.initialFetchAndPreSelect()
  },

  /* component methods */
  methods: {
    // initial loading screen and fetching country / province
    // default to USA and USA provinces (as majority is from USA)
    async initialFetchAndPreSelect() {
      this.initLoading = true

      await this.fetchCountries()

      // preselect country based on form data and fetch provinces
      if (this.billingAddress.country_id && Array.isArray(this.countries)) {
        // set the value in the from drop down
        this.selectedCountry = this.countries.find(
          (country) => country.id === this.billingAddress.country_id,
        )

        await this.fetchProvinces({ id: this.billingAddress.country_id })

        // set initial provinceAbbr
        if (this.billingAddress.province_id) {
          const province = this.findProvinceById(
            this.billingAddress.country_id,
            this.billingAddress.province_id,
          )

          if (province) {
            this.provinceAbbr = province.alphaCode
          }
        }
      }

      // submit back to parent while loading
      if (this.autoSubmit) {
        await this.handleAutoSubmit()
      }

      // clear loading
      this.initLoading = false
    },

    // get all countries
    async fetchCountries() {
      if (this.loadingCountries) {
        return
      }

      this.loadingCountries = true

      try {
        // get all countries
        const res = await fbApi.get('/locations/countries')

        if (!res || !res.body) {
          throw Error('error fetching countries')
        }

        // add countries list to state
        this.countries = res.body.data
      } catch (e) {
        this.error = 'Unable to fetch countries, please refresh the page and try again'
      }

      // clear loading state
      this.loadingCountries = false
    },

    // set just the country_id from the multiselect object
    // then clear and fetch provinces
    setCountryAndFetchProvinces(country) {
      this.billingAddress.country_id = country.id

      // clear provinces on country change
      this.provinceAbbr = ''
      this.billingAddress.province_id = ''
      this.billingAddress.custom_province_alpha_code = ''

      this.fetchProvinces(country)
    },

    // get provinces by country id, but leverage provinceMap for cache
    async fetchProvinces({ id }) {
      // must have country selected, sometimes v-model has not been update
      // when we need to fire, so using data passed in
      if (!id) {
        return
      }

      // check to see if provinces are already cached
      if (this.provinceMap[id]) {
        return
      }

      // fetch provinces
      this.loadingProvinces = true

      try {
        // get all countries
        const res = await fbApi.get(`/locations/countries/${id}/provinces`)

        if (!res || !res.body) {
          throw Error('error fetching provinces')
        }

        // add provinces list to state
        // must be refresh objection for reactivity
        this.provinceMap = {
          ...this.provinceMap,
          [id]: res.body.data,
        }
      } catch (e) {
        this.error = 'Unable to fetch provinces, please refresh the page and try again'
      }

      // clear loading state
      this.loadingProvinces = false
    },

    // emitting to parent component to handle
    handleCancel() {
      this.$emit('billing-cancel-action')
    },

    // helper to get the province based on country / province id
    findProvinceById(countryId, provinceId) {
      if (countryId && provinceId && Array.isArray(this.provinceMap[countryId])) {
        return this.provinceMap[countryId].find((province) => province.id === provinceId)
      }

      return null
    },

    // get province by province abbr code
    findProvinceByAbbr(countryId, provinceCode) {
      // nothing selected
      if (!provinceCode || !countryId) {
        return null
      }

      // missing map
      if (!this.provinceMap || !this.provinceMap[countryId]) {
        return null
      }

      return this.provinceMap[countryId].find((province) => province.alphaCode == provinceCode)
    },

    // billing formatted three ways! needed for braintree and json encoded form submits.
    formatBillingAddress() {
      return {
        // used from form, human readable and snake_case like laravel model
        billingAddress: this.billingAddress,

        // braintree 3d secure formatted
        // @see https://braintree.github.io/braintree-web/current/ThreeDSecure.html#~billingAddress
        threeDSecure: {
          locality: this.billingAddress.city,
          region: this.selectedProvince
            ? this.selectedProvince.alphaCode
            : this.billingAddress.custom_province_alpha_code,
          postalCode: this.billingAddress.postal_code,
          countryCodeAlpha2: this.selectedCountry.alpha2Code,
        },

        // used to submit the full billing address as a single hidden input
        // vs a hidden input for each billing address field
        jsonEncoded: JSON.stringify(this.billingAddress),
      }
    },

    // same as handleSubmit w/o form validation or processing
    // see autoSubmit prop for details
    async handleAutoSubmit() {
      // TODO: sales tax

      // send to parent component after state is settled
      // mirror laravel and relationship format
      this.$nextTick(() => {
        this.$emit('billing-submit-action', this.formatBillingAddress())
      })
    },

    // validation, but allow parent component to handle
    // the billing address
    async handleSubmit(validate) {
      if (this.processing) {
        return
      }

      this.processing = true

      try {
        const results = await validate()

        if (!results.valid) {
          throw Error('validation')
        }

        // TODO: sales tax

        // send to parent component after state is settled
        // mirror laravel and relationship format
        this.$nextTick(() => {
          this.$emit('billing-submit-action', this.formatBillingAddress())
        })
      } catch (e) {
        // no action
      }

      this.processing = false
    },
  },
})
</script>
